import axios from "axios";
import axiosUtil from "@plannel/utils/axios-bigint"
import { format } from "date-fns";
import qs from "qs";

const DATE_PATTERN = "yyyy-MM-dd";
const DP_MODULE = "DP";
const IP_MODULE = "IP";
// const BF_MODULE = "BF";
const RP_MODULE = "RP";
const MP_MODULE = "MP";
const FP_MODULE = "FP";

/*
  세션 만료(401) 전역 신호.

  토큰이 만료돼도 화면은 그대로 떠 있고 요청만 실패한다. 각 화면이 그 실패를 자기 방식으로
  삼키면(대개 catch 후 빈 목록) 사용자는 "데이터가 없다"로 읽는다 — 실제로 워크플로우 패널이
  그렇게 보여서 버그로 신고됐다. 그래서 실패를 한곳에서 잡아 앱에 알린다.

  · **401 만** 본다. 403 은 로그인은 유효한데 권한이 없는 것이라 재로그인 대상이 아니다.
  · 로그인·회원가입은 restApi 가 아니라 raw axios 를 쓴다(auth-service) → 여기 안 걸린다.
    비밀번호 오류의 401 이 "세션 만료"로 둔갑하지 않는다.
  · 화면 하나가 여러 요청을 동시에 날리므로 알림은 **한 번만**. 2xx 가 한 번이라도 오면
    다시 무장한다 — 재로그인 뒤 다음 만료도 잡아야 한다.
  · 에러는 그대로 reject 한다. 기존 화면들의 개별 에러 처리 흐름은 건드리지 않는다.
*/
export const SESSION_EXPIRED_EVENT = "plannel:sessionExpired";
let sessionExpiredNotified = false;

/*
  권한 거절 — 서버는 AccessDeniedException 을 **401 + 본문 "MSG_PermissionDenied"** 로 준다
  (PermessionExceptionAdvice. @PreAuthorize·@RequiresMenu 가 막은 쓰기 등).
  로그인은 유효하므로 세션 만료로 보면 안 된다 — 그랬더니 읽기 전용 사용자가 막힌 저장을 누르면
  로그아웃되고 "토큰 만료"가 떴다. 세션 만료 401(AuthEntryPointJwt)의 본문 message 는 이 값이 아니다.
  앱은 로그아웃 없이 "권한이 없습니다" 안내만 띄운다(App.js). 화면별 에러 처리는 그대로 돈다.
*/
export const PERMISSION_DENIED_EVENT = "plannel:permissionDenied";
const PERMISSION_DENIED = "MSG_PermissionDenied";

export const isPermissionDenied = (error) => {
  const response = error?.response;
  if (response?.status !== 401) return false;
  const data = response.data;
  return data === PERMISSION_DENIED || data?.message === PERMISSION_DENIED;
};

const authHeader = (module = "") => {
  const user = JSON.parse(localStorage.getItem("user"));

  return user && user.accessToken ? {
    "x-tenant-id": user.tenantId,
    Authorization: `${user.type} ${user.accessToken}`,
    "x-module-name": module
  } : {} ;
};

const restApi = (module) => {
  const instance = axios.create({
    // Content-Type 은 반드시 인스턴스 기본값으로 둔다. 아래 인터셉터는 config 병합 "이후"에
    // 돌기 때문에, 여기가 아니라 인터셉터에서 set 하면 요청별로 지정한 Content-Type
    // (FP 의 application/x-www-form-urlencoded, 파일 업로드의 multipart/form-data)을
    // 역으로 덮어쓴다. 기본값이면 요청별 config.headers 가 정상적으로 우선한다.
    headers: { "Content-type": "application/json" },
    transformResponse: axiosUtil.transformResponse,
    transformRequest: axiosUtil.transformRequest,
    timeout: 7200000  // 2 hour
  });

  instance.interceptors.request.use((config) => {
    // 인증 헤더를 인스턴스 생성 시점에 고정하지 않고, 매 요청마다 localStorage 의 최신 토큰으로 붙인다.
    // (예전엔 create 시 1회만 구워서, user 없이 만들어진 인스턴스가 인증 없이 요청을 보냈다)
    const auth = authHeader(module);
    if (typeof config.headers?.set === "function") {
      Object.entries(auth).forEach(([key, value]) => config.headers.set(key, value));
    } else {
      config.headers = { ...config.headers, ...auth };
    }

    config.paramsSerializer = {
      serialize: (params) => {
        for(const item in params) {
          if(Object.prototype.hasOwnProperty.call(params, item)){
            if(params[item] instanceof Date) {
              params[item] = format(params[item], DATE_PATTERN);
            }
          }
        }
        return qs.stringify(params, { arrayFormat: 'repeat' })},
      indexes: null
    } 
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      sessionExpiredNotified = false;   // 세션이 살아 있다 — 다음 만료를 위해 재무장
      return response;
    },
    (error) => {
      if (isPermissionDenied(error)) {
        window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT));
      } else if (error?.response?.status === 401 && !sessionExpiredNotified) {
        sessionExpiredNotified = true;
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default restApi();
export const restApiDP = restApi(DP_MODULE);
export const restApiIP = restApi(IP_MODULE);
// export const restApiBF = restApi(BF_MODULE);
export const restApiRP = restApi(RP_MODULE);
export const restApiMP = restApi(MP_MODULE);
export const restApiFP = restApi(FP_MODULE);